import { useId, type FC, type ReactNode } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from './dialog';
import type { KeyboardShortcutGroup } from './keyboard-shortcut';
import { KeyboardShortcutKeys } from './keyboard-shortcut-keys';

interface KeyboardShortcutsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groups: readonly KeyboardShortcutGroup[];
  title?: string;
  /** One line under the title, e.g. how ⌘ maps on Windows. */
  description?: ReactNode;
}

/**
 * Every hotkey in one place, grouped by where it works. Columns flow so the
 * groups pack tightly from one column on phones to three on wide screens.
 */
export const KeyboardShortcutsDialog: FC<KeyboardShortcutsDialogProps> = ({
  open,
  onOpenChange,
  groups,
  title = 'Keyboard shortcuts',
  description
}) => {
  const headingPrefix = useId();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        // Land on the dialog, not the close button, so it opens without a ring.
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          (event.currentTarget as HTMLElement | null)?.focus();
        }}
        className="flex max-h-[min(44rem,calc(100svh-2rem))] w-[min(60rem,calc(100vw-2rem))] max-w-none flex-col gap-0 overflow-hidden rounded-3xl bg-[var(--z-surface-solid)] p-0 sm:max-w-none"
      >
        <DialogHeader className="gap-1 border-b border-[var(--z-border-subtle)] px-7 pt-6 pb-4 text-left max-sm:px-5">
          <DialogTitle className="text-title-sm">{title}</DialogTitle>
          {description ? (
            <DialogDescription className="text-body text-muted-foreground">
              {description}
            </DialogDescription>
          ) : null}
        </DialogHeader>
        <div className="min-h-0 overflow-y-auto px-7 pt-5 pb-7 max-sm:px-5">
          <div className="columns-1 gap-x-10 sm:columns-2 lg:columns-3">
            {groups.map((group, index) => {
              const headingId = `${headingPrefix}-${index}`;
              return (
                <section
                  key={group.title}
                  aria-labelledby={headingId}
                  className="mb-7 break-inside-avoid"
                >
                  <h3
                    id={headingId}
                    className="mb-2.5 text-eyebrow tracking-[0.12em] text-muted-foreground uppercase"
                  >
                    {group.title}
                  </h3>
                  <ul className="flex flex-col gap-2.5">
                    {group.shortcuts.map((shortcut) => (
                      <li key={shortcut.label} className="flex items-center gap-3">
                        <KeyboardShortcutKeys keys={shortcut.keys} />
                        <span className="min-w-0 text-body text-[var(--z-text-secondary)]">
                          {shortcut.label}
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
